import styles from './Landing.module.css';
import { useState, useEffect, useRef } from 'react';
import Header from '../../components/header/Header';
import CreateButtonCircle from '../../components/createButtonCircle/CreateButtonCircle';
import OnCallUserInfo from '../../components/onCallUserInfo/OnCallUserInfo';
import CreateButtonConfirmationModal from '../../components/createButtonConfirmationModal/CreateButtonConfirmationModal';
import AppInfo from '../../components/appInfo/AppInfo';
import { trackPromise } from 'react-promise-tracker';
import { Helmet } from 'react-helmet';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useOktaAuth } from '@okta/okta-react';
import { serviceConstants } from '../../utils/constant';
import ShowSuccessfulIncidentCreationModal from '../../components/showSuccessfulIncidentCreationModal/ShowSuccessfulIncidentCreationModal';
import ViewIncidentDetails from '../../components/viewIncidentDetails/ViewIncidentDetails';
import IncidentList from '../../components/incidentList/IncidentList';
import CustomerSupportPagerDutyAppInfo from '../../components/customerSupportPagerDutyAppInfo/CustomerSupportPagerDutyAppInfo';
import { useGlobalErrorHandler } from '../../hooks/useGlobalErrorHandler';

import {
  getPagerdutyToken,
  postPagerDutyOnCallEngineer,
  postPagerDutyOnGoingIncidentList,
  postPagerDutyCreateIncident
} from '../../appCall';

interface ILandingProps {
  setSpinnerMessage?: (msg: string | null) => void;
}

interface IOnCallEngineerResponse {
  userList: Array<string>;
  responseCode: string;
  responseMessage: string;
}

interface ISingleIncident {
  incidentId: string;
  incidentUrl: string;
  created_at: string;
  assigneedEngineerName: Array<string>;
  incidentSummary: string;
  status: string;
}

interface IIncidentListResponse {
  incidentList: Array<ISingleIncident>;
  responseCode: 200;
  responseMessage: 'SUCCESS';
}

const Landing = ({ setSpinnerMessage }: ILandingProps) => {
  useGlobalErrorHandler();
  const [showCreateButtonConfirmationModal, setShowCreateButtonConfirmationModal] = useState(false); // show confirmation model
  const [onCallEngineerResponse, setOnCallEngineerResponse] = useState<IOnCallEngineerResponse>();
  const [incidentListResponse, setIncidentListResponse] = useState<IIncidentListResponse>();
  const { oktaAuth, authState } = useOktaAuth();
  const [redirected, setRedirected] = useState(false);
  const [showSuccessfulIncidentCreationModal, setShowSuccessfulIncidentCreationModal] =
    useState(false); // show successful incident creation modal
  const [hasFetched, setHasFetched] = useState(false);
  const modalOpenRef = useRef(false);
  const [summary, setSummary] = useState('');
  const [zoomLink, setZoomLink] = useState('');

  // Function to refresh Okta token if expired
  const refreshOktaToken = async () => {
    try {
      const accessToken = await oktaAuth.tokenManager.get('accessToken');

      if (!accessToken) {
        await oktaAuth.signInWithRedirect();
        return;
      }

      const nowInSeconds = Date.now() / 1000;

      // If token expires in less than 1 minute, renew it
      if (accessToken.expiresAt - nowInSeconds < 60) {
        try {
          await oktaAuth.tokenManager.renew('accessToken');
        } catch (renewError) {
          // Renew failed — probably refresh token expired or invalid
          await oktaAuth.signInWithRedirect();
        }
      }
    } catch (error) {
      toast.error('Error checking Okta token: ' + (error as Error).message, {
        className: styles.toastContentSize
      });
      await oktaAuth.signInWithRedirect();
    }
  };

  useEffect(() => {
    if (!authState?.isAuthenticated) {
      return undefined;
    }

    const intervalId = setInterval(
      () => {
        if (modalOpenRef.current) {
          // Skip refresh while modal is open
          return;
        }

        refreshOktaToken().catch((error) => {
          toast.error('Token refresh error: ' + error.message);
        });
      },
      5 * 60 * 1000
    );

    // Cleanup function to clear the interval
    return () => clearInterval(intervalId);
  }, [authState]);

  useEffect(() => {
    const redirectToLogin = async () => {
      if (authState && !authState.isAuthenticated && !redirected) {
        setRedirected(true); // Prevent further redirects
        try {
          await oktaAuth.signInWithRedirect();
        } catch (error) {
          toast.error((error as Error).message, { className: styles.toastContentSize });
        }
      }
    };

    redirectToLogin().catch((error) => {
      toast.error('Error during redirect: ' + error.message, {
        className: styles.toastContentSize
      });
    });
  }, [authState, oktaAuth, redirected]);

  const fetchTokenAndDataHandler = async () => {
    try {
      if (setSpinnerMessage) {
        setSpinnerMessage('Please wait, setting things up... this may take 4–10 seconds.');
      }
      const pagerTokenDetail = await trackPromise(getPagerdutyToken());
      localStorage.setItem(
        serviceConstants.CS_PAGERDUTY_TOKEN,
        JSON.stringify(pagerTokenDetail.tokenDetail)
      );
      setZoomLink(pagerTokenDetail.zoomLink);
      await fetchOnCallAndIncidentsHandler();
    } catch (error) {
      toast.error((error as Error).message, {
        className: styles.toastContentSize
      });
    } finally {
      setSpinnerMessage?.(null); // Always clear spinner
    }
  };
  const fetchOnCallAndIncidentsHandler = async () => {
    const storedValue = JSON.parse(
      localStorage.getItem(serviceConstants.CS_PAGERDUTY_TOKEN) || 'null'
    );
    if (storedValue) {
      try {
        const [onCallEngineerDetails, incidentList] = await Promise.all([
          postPagerDutyOnCallEngineer(storedValue.access_token),
          postPagerDutyOnGoingIncidentList(storedValue.access_token)
        ]);
        setOnCallEngineerResponse(onCallEngineerDetails);
        setIncidentListResponse(incidentList);
      } catch (error) {
        toast.error((error as Error).message, {
          className: styles.toastContentSize
        });
      }
    } else {
      setOnCallEngineerResponse({ userList: [], responseCode: '200', responseMessage: 'SUCCESS' });
      setIncidentListResponse({ incidentList: [], responseCode: 200, responseMessage: 'SUCCESS' });
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!hasFetched && authState?.isAuthenticated) {
        setHasFetched(true);
        await refreshOktaToken();
        await fetchTokenAndDataHandler();
      }
    };

    fetchInitialData().catch((error) => {
      toast.error('Error fetching initial data: ' + error.message, {
        className: styles.toastContentSize
      });
    });
  }, [authState, hasFetched]);
  const openCreateButtonConfirmationModalHandler = (flag: boolean) => {
    modalOpenRef.current = flag;
    setShowCreateButtonConfirmationModal(flag);
  };

  const closeConfirmationModalHandler = (flag: boolean) => {
    modalOpenRef.current = flag;
    setShowCreateButtonConfirmationModal(flag);
    setSummary(''); // reset input on cancel
  };

  const closeShowSuccessfulIncidentCreationModalHandler = (flag: boolean) => {
    setShowSuccessfulIncidentCreationModal(flag);
  };

  const onSubmitConfirmationModalHandler = async (
    submittedSummary: string,
    isTestIncident: boolean
  ) => {
    const storedValue = JSON.parse(
      localStorage.getItem(serviceConstants.CS_PAGERDUTY_TOKEN) || 'null'
    );
    const tokenData = localStorage.getItem(serviceConstants.OKTA_TOKEN_KEY);
    let userName = '';

    if (tokenData) {
      const parsedData = JSON.parse(tokenData);
      userName = parsedData.idToken?.claims?.sAMAccountName || '';
    }
    if (storedValue) {
      try {
        setSpinnerMessage?.(
          'Please wait, creating an incident for you... this may take 4–5 seconds.'
        );
        const response = await trackPromise(
          postPagerDutyCreateIncident(
            storedValue.access_token,
            submittedSummary,
            userName,
            isTestIncident
          )
        );

        if (response.responseCode === 201) {
          setShowSuccessfulIncidentCreationModal(true);
          setSummary('');
          await fetchOnCallAndIncidentsHandler();
        }
      } catch (error) {
        toast.error((error as Error).message, {
          className: styles.toastContentSize
        });
      } finally {
        setSpinnerMessage?.(null); // Always clear spinner
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>{`Customer Support Panic Button`}</title>
      </Helmet>
      {authState?.isAuthenticated ? (
        <>
          <Header />
          <AppInfo zoomLink={zoomLink} />
          <CreateButtonCircle
            openCreateButtonConfirmationModalCallbackHandler={
              openCreateButtonConfirmationModalHandler
            }
          />
          <OnCallUserInfo userName={onCallEngineerResponse?.userList[0] || null} />

          <ViewIncidentDetails />

          <CustomerSupportPagerDutyAppInfo />

          {incidentListResponse && (
            <IncidentList incidentList={incidentListResponse.incidentList} />
          )}

          <CreateButtonConfirmationModal
            visible={showCreateButtonConfirmationModal}
            initialSummary={summary}
            onSummaryChange={setSummary}
            closeConfirmationModalCallbackHandler={closeConfirmationModalHandler}
            onSubmitConfirmationModalCallbackHandler={onSubmitConfirmationModalHandler}
          />

          <ShowSuccessfulIncidentCreationModal
            zoomLink={zoomLink}
            visible={showSuccessfulIncidentCreationModal}
            closeShowSuccessfulIncidentCreationModalCallbackHandler={
              closeShowSuccessfulIncidentCreationModalHandler
            }
          />
        </>
      ) : (
        <div></div>
      )}
      <div className={styles.emptyDiv} />
      <ToastContainer />
    </>
  );
};

export default Landing;
